'use client'

import {
    createSetting,
    getSettingByName,
    Setting,
    updateSetting,
} from '@/lib/api/utils/settings-api'
import {
    CATALOG_QUICK_CREATE_SETTING,
    DEFAULT_CATALOG_QUICK_CREATE_ENABLED,
    normalizeCatalogQuickCreate,
} from '@/lib/catalog/quick-create-setting'
import {
    DEFAULT_GUIDE_VALIDITY_DAYS,
    GUIDE_VALIDITY_DAYS_SETTING,
    computeGuideValidUntil,
    normalizeGuideValidityDays,
} from '@/lib/guide-validity'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { MdArrowBack } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

type CatalogPricingMode = 'priority' | 'item_only'

const SETTING_NAME = 'catalog_pricing_mode'

const MedicalSettingsPage = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { isHealth, consultation, exam, order } = useServiceLabel()
    const { clientLabel } = useClientLabel()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [existingSetting, setExistingSetting] = useState<Setting | null>(null)
    const [pricingMode, setPricingMode] = useState<CatalogPricingMode>('priority')

    // Validade das guias (dias). Guardado como string para o input aceitar vazio
    // enquanto o operador digita.
    const [savingValidity, setSavingValidity] = useState(false)
    const [validitySetting, setValiditySetting] = useState<Setting | null>(null)
    const [validityDays, setValidityDays] = useState<string>(
        String(DEFAULT_GUIDE_VALIDITY_DAYS)
    )

    // Cadastro rápido de item do catálogo no agendamento.
    const [savingQuickCreate, setSavingQuickCreate] = useState(false)
    const [quickCreateSetting, setQuickCreateSetting] =
        useState<Setting | null>(null)
    const [quickCreateEnabled, setQuickCreateEnabled] = useState(
        DEFAULT_CATALOG_QUICK_CREATE_ENABLED
    )

    useEffect(() => {
        loadSettings()
    }, [user?.companyId])

    const loadSettings = async () => {
        if (!user?.companyId) return
        setLoading(true)

        const [pricingRes, validityRes, quickCreateRes] = await Promise.all([
            getSettingByName(SETTING_NAME, user.companyId),
            getSettingByName(GUIDE_VALIDITY_DAYS_SETTING, user.companyId),
            getSettingByName(CATALOG_QUICK_CREATE_SETTING, user.companyId),
        ])

        if (pricingRes.success && pricingRes.result) {
            setExistingSetting(pricingRes.result)
            setPricingMode(pricingRes.result.value as CatalogPricingMode)
        }
        if (validityRes.success && validityRes.result) {
            setValiditySetting(validityRes.result)
            setValidityDays(
                String(normalizeGuideValidityDays(validityRes.result.value))
            )
        }
        if (quickCreateRes.success && quickCreateRes.result) {
            setQuickCreateSetting(quickCreateRes.result)
            setQuickCreateEnabled(
                normalizeCatalogQuickCreate(quickCreateRes.result.value)
            )
        }

        setLoading(false)
    }

    const handleSave = async () => {
        if (!user?.companyId) return
        setSaving(true)

        try {
            if (existingSetting) {
                const res = await updateSetting(existingSetting.id, { value: pricingMode })
                if (res.success) {
                    setExistingSetting(res.result!)
                    toast.success('Configuração salva com sucesso')
                } else {
                    toast.error('Erro ao salvar configuração')
                }
            } else {
                const res = await createSetting({
                    name: SETTING_NAME,
                    value: pricingMode,
                    companyId: user.companyId,
                    role: 'COMPANY',
                })
                if (res.success) {
                    setExistingSetting(res.result!)
                    toast.success('Configuração salva com sucesso')
                } else {
                    toast.error('Erro ao salvar configuração')
                }
            }
        } catch {
            toast.error('Erro ao salvar configuração')
        }

        setSaving(false)
    }

    const handleSaveValidity = async () => {
        if (!user?.companyId) return
        setSavingValidity(true)

        // Normaliza com a MESMA regra do backend (0 = sem validade; vazio/inválido
        // = default), para o que foi salvo ser exatamente o que a guia vai imprimir.
        const normalized = normalizeGuideValidityDays(validityDays)
        setValidityDays(String(normalized))

        try {
            const res = validitySetting
                ? await updateSetting(validitySetting.id, {
                      value: String(normalized),
                  })
                : await createSetting({
                      name: GUIDE_VALIDITY_DAYS_SETTING,
                      value: String(normalized),
                      companyId: user.companyId,
                      role: 'COMPANY',
                  })

            if (res.success) {
                setValiditySetting(res.result!)
                toast.success(
                    isHealth
                        ? 'Validade das guias salva com sucesso'
                        : 'Validade dos comprovantes salva com sucesso'
                )
            } else {
                toast.error('Erro ao salvar a validade das guias')
            }
        } catch {
            toast.error('Erro ao salvar a validade das guias')
        }

        setSavingValidity(false)
    }

    const handleSaveQuickCreate = async () => {
        if (!user?.companyId) return
        setSavingQuickCreate(true)

        try {
            const value = String(quickCreateEnabled)
            const res = quickCreateSetting
                ? await updateSetting(quickCreateSetting.id, { value })
                : await createSetting({
                      name: CATALOG_QUICK_CREATE_SETTING,
                      value,
                      companyId: user.companyId,
                      role: 'COMPANY',
                  })

            if (res.success) {
                setQuickCreateSetting(res.result!)
                toast.success('Cadastro rápido de catálogo salvo com sucesso')
            } else {
                toast.error('Erro ao salvar o cadastro rápido de catálogo')
            }
        } catch {
            toast.error('Erro ao salvar o cadastro rápido de catálogo')
        }

        setSavingQuickCreate(false)
    }

    // Prévia: como ficaria uma guia emitida HOJE com a configuração digitada.
    const previewValidUntil = computeGuideValidUntil(
        new Date(),
        normalizeGuideValidityDays(validityDays)
    )

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        )
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                    <MdArrowBack size={20} />
                </button>
                <h1 className="text-2xl font-bold">
                    {isHealth
                        ? 'Configurações Médicas'
                        : 'Configurações de Atendimento'}
                </h1>
            </div>

            {/* Modo de preços do catálogo */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-2">Preços do Catálogo via IA</h2>
                    <p className="text-sm text-base-content/60 mb-6">
                        Define como os preços dos itens do catálogo serão utilizados durante o atendimento via inteligência artificial.
                    </p>

                    <div className="flex flex-col gap-4">
                        <label
                            className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${pricingMode === 'priority'
                                    ? 'border-secondary bg-secondary/5'
                                    : 'border-base-300 hover:border-base-content/30'
                                }`}
                        >
                            <input
                                type="radio"
                                name="pricingMode"
                                className="radio radio-secondary mt-1"
                                checked={pricingMode === 'priority'}
                                onChange={() => setPricingMode('priority')}
                            />
                            <div>
                                <span className="font-medium">
                                    Priorizar preços de parceiros e profissionais
                                </span>
                                <p className="text-sm text-base-content/60 mt-1">
                                    Quando o item possuir preços definidos em parceiros, esses serão exibidos primeiro.
                                    Caso contrário, serão usados os preços dos profissionais.
                                    Se nenhum dos dois estiver definido, será utilizado o preço base do item.
                                </p>
                            </div>
                        </label>

                        <label
                            className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${pricingMode === 'item_only'
                                    ? 'border-secondary bg-secondary/5'
                                    : 'border-base-300 hover:border-base-content/30'
                                }`}
                        >
                            <input
                                type="radio"
                                name="pricingMode"
                                className="radio radio-secondary mt-1"
                                checked={pricingMode === 'item_only'}
                                onChange={() => setPricingMode('item_only')}
                            />
                            <div>
                                <span className="font-medium">
                                    Utilizar sempre o preço base do item
                                </span>
                                <p className="text-sm text-base-content/60 mt-1">
                                    O preço apresentado ao {clientLabel} será sempre o definido no próprio item do catálogo.
                                    Parceiros e profissionais serão exibidos apenas como opções de quem realizará
                                    o serviço, sem preço individual.
                                </p>
                            </div>
                        </label>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            className="btn btn-secondary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            Salvar
                        </button>
                    </div>
                </div>
            </div>

            {/* Cadastro rápido de item do catálogo */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-2">
                        Cadastro Rápido de Catálogo
                    </h2>
                    <p className="text-sm text-base-content/60 mb-6">
                        Permite cadastrar um item de {consultation}, {exam} ou{' '}
                        {order} sem sair da tela de agendamento, quando a
                        busca no catálogo não encontra nada.
                    </p>

                    <label className="flex items-start gap-4 p-4 rounded-lg border border-base-300 cursor-pointer transition-colors hover:border-base-content/30">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-secondary mt-1"
                            checked={quickCreateEnabled}
                            onChange={e =>
                                setQuickCreateEnabled(e.target.checked)
                            }
                        />
                        <div>
                            <span className="font-medium">
                                Permitir cadastrar item do catálogo no
                                agendamento
                            </span>
                            <p className="text-sm text-base-content/60 mt-1">
                                A busca sem resultado passa a oferecer
                                "Adicionar", com nome, preço de custo, preço de
                                venda e margem. Antes de criar, o sistema
                                confere se o item já existe no catálogo — se
                                existir, oferece atrelá-lo ao parceiro em vez de
                                duplicar. Desligado, o catálogo só é alterado
                                pela tela de cadastro.
                            </p>
                        </div>
                    </label>

                    <div className="mt-6 flex justify-end">
                        <button
                            className="btn btn-secondary"
                            onClick={handleSaveQuickCreate}
                            disabled={savingQuickCreate}
                        >
                            {savingQuickCreate && (
                                <span className="loading loading-spinner loading-sm"></span>
                            )}
                            Salvar
                        </button>
                    </div>
                </div>
            </div>

            {/* Validade das guias */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-2">
                        {isHealth
                            ? 'Validade das Guias'
                            : 'Validade dos Comprovantes'}
                    </h2>
                    <p className="text-sm text-base-content/60 mb-6">
                        Prazo de validade impresso{' '}
                        {isHealth ? 'nas guias' : 'nos comprovantes'} de {exam},{' '}
                        {consultation} e {order}. A data é contada a partir
                        da{' '}
                        <strong>emissão do documento</strong> (não da data de
                        impressão): reimprimir{' '}
                        {isHealth ? 'uma guia antiga' : 'um comprovante antigo'}{' '}
                        não renova a
                        validade. Orçamentos não usam esta configuração — eles têm
                        validade própria.
                    </p>

                    <div className="form-control w-full max-w-xs">
                        <label className="label pb-1" htmlFor="guide-validity-days">
                            <span className="label-text font-medium">
                                Validade padrão{' '}
                                {isHealth ? 'das guias' : 'dos comprovantes'} (em
                                dias)
                            </span>
                        </label>
                        <input
                            id="guide-validity-days"
                            type="number"
                            min={0}
                            max={3650}
                            className="input input-bordered w-full"
                            value={validityDays}
                            onChange={e => setValidityDays(e.target.value)}
                            placeholder={String(DEFAULT_GUIDE_VALIDITY_DAYS)}
                        />
                        <label className="label pt-1">
                            <span className="label-text-alt text-base-content/60">
                                Use <strong>0</strong> para não imprimir validade
                                nenhuma.
                            </span>
                        </label>
                    </div>

                    <div className="mt-4 p-3 rounded-lg bg-base-200/60 text-sm">
                        {previewValidUntil ? (
                            <>
                                {isHealth ? 'Uma guia emitida' : 'Um comprovante emitido'}{' '}
                                hoje seria válido até{' '}
                                <strong>
                                    {previewValidUntil.toLocaleDateString('pt-BR')}
                                </strong>
                                .
                            </>
                        ) : (
                            <>
                                As guias serão impressas <strong>sem validade</strong>.
                            </>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            className="btn btn-secondary"
                            onClick={handleSaveValidity}
                            disabled={savingValidity}
                        >
                            {savingValidity && (
                                <span className="loading loading-spinner loading-sm"></span>
                            )}
                            Salvar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MedicalSettingsPage
